"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Rocket,
  Sparkles,
  Target,
  TrendingUp,
  Building2,
  Shield,
  Heart,
  ArrowRight,
  Crown,
  Mic,
  Layers,
  Compass,
  Lock,
  BookOpen,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { ArchitectureDiagramBlock } from "@/lib/blocks/architecture-diagram-block";
import { CounterBlock } from "@/lib/blocks/counter-block";
import { PillarGridBlock } from "@/lib/blocks/pillar-grid-block";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

const EVIDENCE = [
  { metric: "10", suffix: "+", label: "Years of Building", sub: "Single organization sustained", accent: "purple" },
  { metric: "18", suffix: "", label: "Countries", sub: "Three continents", accent: "cyan" },
  { metric: "300", suffix: "+", label: "Elite Clients", sub: "Served with discretion", accent: "emerald" },
  { metric: "0", suffix: "", label: "Security Breaches", sub: "15+ years clean record", accent: "amber-gold" },
];

const PHASES = [
  { num: "01", title: "Security Consultancy", body: "Sovereign-grade encryption, secure communications, and threat intelligence for government and private sector clients. The security posture that became the sovereign security architecture.", accent: "red" },
  { num: "02", title: "Intelligence & Perception Platform", body: "Evolution from defensive security to proactive intelligence. The early-warning system. The perception engine. The leap from protecting information to understanding and shaping the information environment.", accent: "purple" },
  { num: "03", title: "Political & Electoral Operations", body: "Application to the highest-stakes domain: democratic elections. Booth-level intelligence. Fifteen victories in eighteen engagements. Nine hundred million-plus voters influenced.", accent: "cyan" },
  { num: "04", title: "Full-Spectrum Digital Conglomerate", body: "The culmination: integration of all capabilities into a unified system. The world&apos;s first Digital Conglomerate for Strategic Sovereignty. Eighteen countries. Three hundred-plus elite clients.", accent: "emerald" },
];

const PRINCIPLES = [
  { num: "01", title: "Building Over Starting", body: "Starting is a moment. Building is a discipline. Starting requires vision, courage, and capital. Building requires systems, protocols, accountability, and operational excellence that converts ambition into sustained outcomes." },
  { num: "02", title: "Systems Over Charisma", body: "Organizations that endure are built on systems, not charisma. On protocols, not heroics. On the discipline of building in the dark rather than performing in the spotlight." },
  { num: "03", title: "Duration Over Speed", body: "Anyone can produce a good quarter. Few can produce a good decade. The proof of operational excellence is duration. The discipline of sustained, verified operation through every global disruption." },
  { num: "04", title: "Capability Over Visibility", body: "Market expansion through capability demonstration. Clients who engage because they need what the organization has built. Not because the organization has marketed itself in those geographies." },
  { num: "05", title: "Resilience Built In", body: "Building something that works under favorable conditions is common. Building something that thrives under hostile conditions — that grows when others contract — is the hallmark of systems over circumstances." },
  { num: "06", title: "Build in the Dark", body: "The most consequential work is done without an audience. The discretion doctrine: outcomes are the currency of credibility, and visibility is a choice, not a requirement." },
];

const TRAITS = [
  { title: "Construct, Don't Acquire", body: "Build every component from scratch, test against sovereign-scale requirements, refine through continuous improvement, and prove across eighteen countries before claiming it works. The builder's approach: durability of outcome." },
  { title: "Own Every Line", body: "Every line of code proprietary. Every algorithm auditable. Every advantage exclusive. Zero third-party dependencies. Zero licensed components. Zero external dependencies that could become points of failure." },
  { title: "Compound the Investment", body: "Each year of research and development produces capabilities that the previous year did not have. The technology investment compounds. The capabilities compound. The advantage compounds. This is the return on building." },
];

export default function EntrepreneurshipPage() {
  return (
    <>
      <PageHero
        icon={Rocket}
        overline="Entrepreneurship & Building"
        overlineClassName="text-purple"
        title={
          <>
            The Discipline of Constructing <span className="text-gradient-premium">Consequential Organizations.</span>
          </>
        }
        subtitle="Entrepreneurship is not a career choice. It is an operating discipline — the sustained, systematized, accountable practice of constructing organizations that endure beyond the founder, outlast the market cycle, and produce verified outcomes across decades."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Expertise", href: "/expertise/" }, { label: "Entrepreneurship & Building" }]}
        badge="Build, don't launch"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-5`}>
              <Sparkles className="size-3.5 text-purple" />
              <span className="text-overline text-purple">The Builder&apos;s Mindset</span>
            </div>
            <h2 className="text-display-md mb-5">
              Building is not the same as starting.
            </h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              The startup world celebrates founders. The business press celebrates launches. The venture capital ecosystem celebrates fundraising milestones. None of these celebrate what actually matters: the sustained, systematized, accountable practice of constructing an organization that produces verified outcomes year after year, through market disruptions, global crises, leadership transitions, and the relentless entropy that destroys most ventures within their first five years.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              The organization Dr. Jyoti Kush co-built did not merely survive the COVID era. It grew, excelled, and evolved while most organizations were suffering. This is the builder&apos;s mindset in practice: the discipline of constructing something that endures when everything else does not.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {EVIDENCE.map((e, i) => {
              const a = ACCENT[e.accent];
              return (
                <motion.div
                  key={e.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
                >
                  <div className={`text-display-sm font-bold ${a.text} leading-none mb-2`}>{e.metric}{e.suffix}</div>
                  <div className="text-caption font-bold uppercase tracking-wider mb-1">{e.label}</div>
                  <div className="text-caption text-muted-foreground">{e.sub}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Building2 className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Four Phases</span>
          </div>
          <h2 className="text-display-md mb-4">From security consultancy to digital conglomerate.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Four distinct phases of metamorphosis, each producing a fundamentally more capable entity than the one before. Architectural evolution, not incremental growth.
          </p>
        </div>
        <div className="space-y-4">
          {PHASES.map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.num}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="grid lg:grid-cols-[auto_1fr] gap-5 items-center">
                  <div className={`size-14 rounded-2xl bg-gradient-sovereign flex items-center justify-center shadow-xl ring-2 ${a.ring}`}>
                    <span className="text-display-sm font-bold text-white">{p.num}</span>
                  </div>
                  <div>
                    <h3 className={`text-display-sm mb-2 ${a.text}`}>{p.title}</h3>
                    <p className="text-body-md text-muted-foreground leading-relaxed">{p.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <Target className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">The Builder&apos;s Principles</span>
          </div>
          <h2 className="text-display-md mb-4">Six principles for organizations that endure.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The principles that distinguish organizations that survive from organizations that thrive across decades.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {PRINCIPLES.map((p, i) => {
            const accents = ["red", "purple", "cyan", "emerald", "amber-gold", "denim"];
            const a = ACCENT[accents[i]];
            return (
              <motion.div
                key={p.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <div className="relative">
                  <div className={`text-display-sm font-bold ${a.text} opacity-30 leading-none mb-3`}>{p.num}</div>
                  <h3 className={`text-heading-md mb-3 ${a.text}`}>{p.title}</h3>
                  <p className="text-body-sm text-muted-foreground leading-relaxed">{p.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <Shield className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">The Platform Development Philosophy</span>
          </div>
          <h2 className="text-display-md mb-4">The infrastructure that no competitor can replicate.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The most misunderstood aspect of building at this scale is the platform development philosophy. The antithesis of the modern startup approach.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {TRAITS.map((t, i) => {
            const accents = ["red", "purple", "cyan"];
            const a = ACCENT[accents[i]];
            return (
              <motion.div
                key={t.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={`glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
              >
                <h3 className={`text-heading-md mb-3 ${a.text}`}>{t.title}</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed">{t.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20" id="entrepreneurial-journey" ariaLabel="Entrepreneurial Journey Map">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <Compass className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">The Entrepreneurial Journey Map</span>
          </div>
          <h2 className="text-display-md mb-4">Six satellites. One center. The builder&apos;s path.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The signature architecture. Six operational stages, one central discipline, one verified outcome: the construction of consequential organizations that endure beyond the founder, outlast the market cycle, and produce verified outcomes across decades.
          </p>
        </div>
        <ArchitectureDiagramBlock
          center={{
            label: "The Builder's Discipline",
            body: "Sustained, systematized, accountable practice. Construction, not launch. Duration, not speed.",
            icon: <Rocket className="size-5" />,
          }}
          satellites={[
            { label: "Conceive", body: "Vision, market gap, structural insight. The discipline of identifying the problem worth solving.", icon: <Compass className="size-4" />, accent: "red" },
            { label: "Construct", body: "Platform, team, protocol. The discipline of building every component from first principles.", icon: <Building2 className="size-4" />, accent: "purple" },
            { label: "Validate", body: "Sovereign-scale deployment. The discipline of proving the system under maximum pressure.", icon: <Target className="size-4" />, accent: "cyan" },
            { label: "Compound", body: "Capability that grows year over year. The discipline of letting the investment accumulate.", icon: <TrendingUp className="size-4" />, accent: "emerald" },
            { label: "Sustain", body: "Through every disruption, every crisis, every leadership transition. The discipline of durability.", icon: <Shield className="size-4" />, accent: "amber-gold" },
            { label: "Transfer", body: "Discretion, succession, institutionalization. The discipline of building that outlasts the founder.", icon: <Lock className="size-4" />, accent: "denim" },
          ]}
          connections={[
            { from: -1, to: 0 }, { from: -1, to: 1 }, { from: -1, to: 2 },
            { from: -1, to: 3 }, { from: -1, to: 4 }, { from: -1, to: 5 },
          ]}
        />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Building2 className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Build-Through-Disruption Record</span>
          </div>
          <h2 className="text-display-md mb-4">Ten years. Four phases. One uninterrupted build.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Every metric traces to operational data. The record speaks with the authority only verified outcomes can command — through pandemic, supply chain collapse, geopolitical disruption, and global market volatility.
          </p>
        </div>
        <CounterBlock
          columns={4}
          stats={[
            { value: 10, suffix: "+", label: "Years of Building", description: "Single organization sustained through every disruption", accent: "purple" },
            { value: 18, label: "Countries", description: "Three continents, one unified build", accent: "cyan" },
            { value: 300, suffix: "+", label: "Elite Clients", description: "Served with absolute discretion", accent: "emerald" },
            { value: 0, label: "Security Breaches", description: "Across fifteen-plus years", accent: "amber-gold" },
            { value: 9, label: "Proprietary Platforms", description: "Built in-house, zero third-party dependencies", accent: "red" },
            { value: 60, suffix: "+", label: "Integrated Services", description: "Across the five Penta P pillars", accent: "denim" },
          ]}
        />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <Heart className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">The Sustainability Doctrine</span>
          </div>
          <h2 className="text-display-md mb-4">Six forces that determine whether organizations last.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The forces that distinguish organizations that survive from organizations that thrive across decades. The doctrine applied to any organization that demands durability over hype.
          </p>
        </div>
        <PillarGridBlock
          columns={3}
          variant="card"
          pillars={[
            { title: "Mission Over Margin", body: "The organization whose mission is real sustains when the easy exit is available. Margin follows mission. The builder's discipline starts with why the organization exists, not what it sells.", icon: <Target className="size-5" />, accent: "red" },
            { title: "Process Over Personality", body: "Organizations that endure have process. The process outlives the personality. The founder's presence is not the load-bearing structure of the organization.", icon: <Compass className="size-5" />, accent: "purple" },
            { title: "Compounding Investment", body: "Each year's research produces capabilities the previous year did not have. The technology investment compounds. The capability compounds. The advantage compounds.", icon: <TrendingUp className="size-5" />, accent: "cyan" },
            { title: "Talent Investment", body: "The teams that produce the outcomes. Selection optimized for the traits that produce operational reliability. The 70% Operating Model in practice. Institutional knowledge preserved.", icon: <Heart className="size-5" />, accent: "emerald" },
            { title: "Resilience Architecture", body: "Building something that works under favorable conditions is common. Building something that thrives under hostile conditions — that grows when others contract — is the hallmark of systems over circumstances.", icon: <Shield className="size-5" />, accent: "amber-gold" },
            { title: "Discretion Doctrine", body: "The most consequential work is done without an audience. Visibility is a choice, not a requirement. Outcomes are the currency of credibility, and credibility is built quietly.", icon: <BookOpen className="size-5" />, accent: "denim" },
          ]}
        />
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-purple/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-cyan/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Sparkles className="size-10 text-purple mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Engage the builder&apos;s expertise.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Advisory, keynotes, and consulting for entrepreneurs, executives, and boards. The discipline of building is available to every organization that demands durability over hype.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Begin an Engagement
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/expertise/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Layers className="size-4" />
                All Expertise Domains
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Expertise Hub", description: "The complete map of fifteen expertise domains. One integrated operating system.", href: "/expertise/", icon: Rocket, accent: "purple" },
          { title: "AI, Technology & The Future", description: "Governing intelligence at sovereign scale. The AI architecture the future demands.", href: "/expertise/ai-technology-future/", icon: TrendingUp, accent: "cyan" },
          { title: "Global Operations", description: "Sovereign-scale execution across continents. Eighteen countries, one unified command.", href: "/expertise/global-operations/", icon: Building2, accent: "emerald" },
          { title: "About Dr. Jyoti Kush", description: "The physician who became an operator. A decade of sovereign-scale leadership.", href: "/about/", icon: Crown, accent: "amber-gold" },
        ]}
      />
    </>
  );
}
